<template>
  <div>
    <div v-if="showDeleteButton" class="flex justify-end items-center pb3">
      <ButtonV2
        color="red"
        :reference="'DELETE'"
        rounded
        class="px2 py1"
        :action="showDeleteContactModal"
      />
    </div>
    <div class="grid gap3 form-row">
      <FormInput
        :id="`emergency_firstname_${index}`"
        v-model:value="modelValue.firstname"
        v-model:errorMessage="errors.firstname"
        class="grid-col-4"
        type="text"
        label="FIRST_NAME"
        placeholder="YOUR_FIRST_NAME"
      />

      <FormInput
        :id="`emergency_lastname_${index}`"
        v-model:value="modelValue.lastname"
        v-model:errorMessage="errors.lastname"
        class="grid-col-4"
        type="text"
        label="LAST_NAME"
        placeholder="YOUR_LAST_NAME"
      />

      <FormInput
        :id="`emergency_email_${index}`"
        v-model:value="modelValue.email"
        v-model:errorMessage="errors.email"
        class="grid-col-4"
        type="email"
        label="EMAIL"
      />

      <FormInput
        :id="`emergency_telephone_${index}`"
        v-model:value="modelValue.telephone"
        v-model:errorMessage="errors.telephone"
        class="grid-col-4"
        type="tel"
        label="PHONE_NUMBER"
        placeholder="MOBILE_PLACEHOLDER"
      />
    </div>

    <div class="pt3">
      <div class="flex justify-between col-12 xs-hide sm-hide gap1">
        <ButtonV2
          :disabled="disableFormButtons"
          color="light"
          :action="resetContact"
          :reference="'CANCEL'"
          rounded
          class="col-6 px2 py1"
        />
        <ButtonV2
          :disabled="disableFormButtons"
          color="dark"
          :action="saveContact"
          :reference="'SAVE'"
          rounded
          class="col-6 px2 py1"
        />
      </div>
      <div class="flex flex-column col-12 gap2 md-hide lg-hide gap1">
        <ButtonV2
          :disabled="disableFormButtons"
          color="light"
          :action="resetContact"
          :reference="'CANCEL'"
          rounded
          class="col-12 px2 py1"
        />
        <ButtonV2
          :disabled="disableFormButtons"
          color="dark"
          :action="saveContact"
          :reference="'SAVE'"
          rounded
          class="col-12 px2 py1"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { isEqual } from 'lodash';
import { useStore } from 'vuex';
import FormInput from '@/components/inputs/FormInput';
import ButtonV2 from '@/components/elements/ButtonV2';
import { getReferencelabel } from '@/mixins/utils';
import { validateForm } from '@/mixins/formValidation';

export default {
  name: 'EmergencyContactForm',
  components: { FormInput, ButtonV2 },
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    }
  },
  emits: ['save-contact', 'reset-contact', 'show-delete-contact-modal'],
  setup (props, { emit }) {
    const store = useStore();

    const errors = ref({});

    const emergencyContacts = computed(() => store.state.user.emergency_contacts || []);

    const emergencyContactFormValidations = {
      firstname: {
        rules: ['required', 'min:2'],
        regex: /^([A-Za-z-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/,
        errorMessages: {
          required: 'FIRST_NAME_ERROR',
          min: 'FIRST_NAME_MORE_2',
          regex: 'FIRST_NAME_ONLY_LETTERS'
        }
      },
      lastname: {
        rules: ['required', 'min:2'],
        regex: /^([A-Za-z-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/,
        errorMessages: {
          required: 'LASTNAME_ERROR',
          min: 'LASTNAME_MORE_2',
          regex: 'LASTNAME_ONLY_LETTERS'
        }
      },
      email: {
        rules: ['required', 'email'],
        errorMessages: {
          required: 'ERROR_EMAIL_EMPTY',
          email: 'ERROR_WRONG_EMAIL'
        }
      },
      telephone: {
        rules: ['required', 'phone_number'],
        errorMessages: { required: 'ERROR_PHONE_EMPTY' }
      }
    };

    const disableFormButtons = computed(() => {
      if (isEqual(props.modelValue, emergencyContacts.value[props.index])) {
        return true;
      }

      return false;
    });

    const saveContact = () => {
      if (disableFormButtons.value) {
        return;
      }

      errors.value = validateForm(props.modelValue, emergencyContactFormValidations);
      if (Object.keys(errors.value).length) {
        return;
      }
      emit('save-contact');
    };

    const resetContact = () => {
      if (disableFormButtons.value) {
        return;
      }

      errors.value = {};
      emit('reset-contact');
    };

    const showDeleteButton = computed(() => emergencyContacts.value[props.index] && emergencyContacts.value.length > 1);

    const showDeleteContactModal = () => {
      errors.value = {};
      emit('show-delete-contact-modal');
    };

    return {
      getReferencelabel,
      errors,
      saveContact,
      resetContact,
      showDeleteContactModal,
      disableFormButtons,
      showDeleteButton
    };
  }
};
</script>
